import { Box } from '@mui/material';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    ReferenceInput,
    AutocompleteInput,
    TextField,
    Datagrid,
    DeleteButton,
    SelectField,
    useRecordContext,
} from 'react-admin';
import { COUNTRIES, USER_SUBTYPES, USER_TYPES } from '../choices';
import ManyToManyInput from './forms/ManyToManyInput';
import OnlySaveToolbar from './toolbar/OnlySaveToolbar';

const nestedDatagridSx = {
    '& .RaDatagrid-tableWrapper': { overflowX: 'auto' },
} as const;

const EditForm = () => {
    const record = useRecordContext();
    const deleteMeta = {
        resource: 'users' as const,
        resourceId: record.id,
    };

    return (
        <SimpleForm toolbar={<OnlySaveToolbar />}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                    columnGap: 3,
                    alignItems: 'start',
                    width: '100%',
                    maxWidth: 960,
                }}
            >
                <TextInput source='id' disabled fullWidth />
                <TextInput source='username' disabled fullWidth />
                <TextInput source='email' disabled fullWidth />
                <BooleanInput source='has_usable_password' disabled fullWidth />
                <TextInput source='first_name' fullWidth />
                <TextInput source='last_name' fullWidth />
                
                <SelectInput
                    source='type'
                    choices={Array.from(USER_TYPES)}
                    required={true}
                    fullWidth
                />
                <SelectInput
                    source='subtype'
                    choices={Array.from(USER_SUBTYPES)}
                    fullWidth
                />
                
                <TextInput source='language' fullWidth />
                <SelectInput source='country' choices={COUNTRIES} required={true} fullWidth />
                
                <TextInput source='phone' fullWidth sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }} />
                
                <BooleanInput source='verified_email' fullWidth />
                <BooleanInput source='receive_notifications' fullWidth />
                <BooleanInput source='receive_whatsapp_notifications' label='WhatsApp notifications' fullWidth />
                <BooleanInput source='validated_type' label='Validated type' fullWidth />
                <TextInput
                    source='date_joined'
                    label='Date joined'
                    disabled
                    fullWidth
                  
                />
                <TextInput
                    source='last_login'
                    label='Last login'
                    disabled
                    fullWidth
                  
                />
            </Box>
            <ManyToManyInput
                reference='groups'
                referenceField='group_id'
                label='Groups'
                tableChildren={
                    <Datagrid
                        isRowSelectable={() => false}
                        bulkActionButtons={false}
                        size='small'
                        sx={nestedDatagridSx}
                    >
                        <TextField source='id' label='ID' />
                        <TextField source='name' label='Name' />
                        <DeleteButton mutationOptions={{ meta: deleteMeta }} redirect={false} />
                    </Datagrid>
                }
            />
            <ManyToManyInput
                reference='internal-app-instances'
                referenceField='app_id'
                label='Internal Apps Instances'
                addChildren={
                    <ReferenceInput label='Internal Apps Instances' source='app_id' reference='tenant/app-instances'>
                        <AutocompleteInput
                            optionText={(appInstance) =>
                                `${appInstance.app.name} - ${
                                    COUNTRIES.find((c) => c.id === appInstance.country_id)?.name
                                }`
                            }
                            optionValue='id'
                        />
                    </ReferenceInput>
                }
                tableChildren={
                    <Datagrid
                        isRowSelectable={() => false}
                        bulkActionButtons={false}
                        size='small'
                        sx={{
                            ...nestedDatagridSx,
                            '& .cell-app-name': {
                                maxWidth: 320,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                        }}
                    >
                        <TextField source='id' label='ID' />
                        <TextField source='app.name' label='App' cellClassName='cell-app-name' />
                        <SelectField source='country_id' choices={COUNTRIES} label='Country' />
                        <DeleteButton mutationOptions={{ meta: deleteMeta }} redirect={false} />
                    </Datagrid>
                }
            />
        </SimpleForm>
    );
};

const UserEdit = (props: any) => {
    return (
        <Edit title='Edit a User' {...props}>
            <EditForm />
        </Edit>
    );
};

export default UserEdit;
